import { registerLocaleData } from '@angular/common';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import localeEn from '@angular/common/locales/en';
import localeVi from '@angular/common/locales/vi';
import {
    ApplicationConfig,
    ErrorHandler,
    inject,
    isDevMode,
    LOCALE_ID,
    provideAppInitializer,
    provideEnvironmentInitializer,
    provideZoneChangeDetection,
} from '@angular/core';
import { MatDatepickerIntl } from '@angular/material/datepicker';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
    provideRouter,
    withInMemoryScrolling,
    withNavigationErrorHandler,
} from '@angular/router';
import {
    FuseProviderConfig,
    getFuseHttpInterceptors,
    provideFuse,
} from '@fuse';
import { FuseLoadingService } from '@fuse/services/loading';
import { provideTransloco, TranslocoService } from '@jsverse/transloco';
import * as Sentry from '@sentry/angular';
import { appRoutes } from 'app/app.routes';
import { provideIcons } from 'app/core/icons/icons.provider';
import {
    authInterceptors,
    provideAuth,
} from 'app/core/providers/auth.provider';
import { provideNotificationInbox } from 'app/core/providers/notification-inbox.provider';
import { ChunkLoadRecoveryService } from 'app/core/services/chunk-load-recovery.service';
import { DocumentLangService } from 'app/core/services/document-lang.service';
import { OrgContextService } from 'app/core/services/org-context.service';
import { SafeBackService } from 'app/core/services/safe-back.service';
import { SplashScreenGuardService } from 'app/core/services/splash-screen-guard.service';
import { TenantThemeService } from 'app/core/services/tenant-theme.service';
import { TimezoneService } from 'app/core/services/timezone.service';
import { firstValueFrom } from 'rxjs';
import { AppDatepickerIntl } from './core/i18n/app-datepicker-intl';

import { AppPaginatorIntl } from './core/i18n/app-paginator-intl';
import { TranslocoHttpLoader } from './core/transloco/transloco.http-loader';

import { getAngularLocale, NERA_TIMEZONE } from 'nera-common/angular';
registerLocaleData(localeEn);
registerLocaleData(localeVi);

const initialLanguage = localStorage.getItem('appLanguage') || 'vn';

// Fuse config, consumed by provideFuse() below.
const fuseProviderConfig: FuseProviderConfig = {
    fuse: {
        layout: 'classy',
        scheme: 'light',
        screens: {
            sm: '600px',
            md: '960px',
            lg: '1280px',
            xl: '1440px',
        },
        theme: 'theme-default',
        themes: [
            {
                id: 'theme-default',
                name: 'Default',
            },
        ],
    },
};

export const appConfig: ApplicationConfig = {
    providers: [
        // PFL-1679: Fuse's global loading bar defaults to "auto" mode, which
        // makes fuseLoadingInterceptor toggle it on every HTTP request —
        // stacking on top of each widget's own honest MetricSurface loading
        // state. Turning auto mode off here (rather than only via the
        // [autoMode] input on <fuse-loading-bar>) also covers boot-window
        // requests (transloco i18n/vn.json, /auth/session/me) that fire
        // before any layout has instantiated the bar. Auth-only layouts
        // that still want the global bar re-enable it explicitly via
        // [autoMode]="true" on their own <fuse-loading-bar>.
        //
        // FIRST provider on purpose: environment initializers run in
        // provider order, and the TenantThemeService / OrgContextService
        // initializers below issue boot HTTP requests. Registered after
        // them (as it used to be), their request went out while auto mode
        // was still on and flipped show$ to true — the app.config.spec
        // flake (it only passed when another spec had already cached the
        // org lookup in sessionStorage, so no request was sent).
        provideEnvironmentInitializer(() =>
            inject(FuseLoadingService).setAutoMode(false)
        ),

        // #1398: widens the vendored FuseSplashScreenService's hide trigger
        // (NavigationEnd only) so a guard-redirect/error first navigation
        // doesn't leave the boot splash stuck over a rendered page.
        provideEnvironmentInitializer(() => inject(SplashScreenGuardService)),

        // Sentry
        { provide: ErrorHandler, useValue: Sentry.createErrorHandler() },

        { provide: LOCALE_ID, useValue: getAngularLocale(initialLanguage) },
        // The shared Angular surfaces (the date pipe, the shared table) live
        // in nera-common and may not depend on an app service — the resolved
        // display zone travels through this port instead.
        {
            provide: NERA_TIMEZONE,
            // inject() must run inside the factory (injection context); calling
            // it inside the returned zone() would throw NG0203 at pipe-eval
            // time and silently break every embedded row view after the first.
            useFactory: () => {
                const timezone = inject(TimezoneService);
                return { zone: () => timezone.zone() };
            },
        },
        { provide: MatPaginatorIntl, useClass: AppPaginatorIntl },
        { provide: MatDatepickerIntl, useClass: AppDatepickerIntl },
        // Coalesce zone-patched event/turn CD passes — without this every
        // patched async task (each XHR, timer, DOM event) schedules its own
        // ApplicationRef.tick(). runCoalescing additionally collapses the
        // per-component schedule ticks into one pass.
        provideZoneChangeDetection({
            eventCoalescing: true,
            runCoalescing: true,
        }),
        // Defer @angular/animations out of the initial bundle; the module
        // loads on first use instead of blocking bootstrap.
        provideAnimationsAsync(),
        // PFL-1679: single provideHttpClient() for the whole app. Previously
        // app.config.ts, auth.provider.ts and fuse.provider.ts each called
        // provideHttpClient() separately, which fired every interceptor
        // (auth, tenant, language, api-error, fuse loading) multiple times
        // per request — the visible symptom was the loading spinner
        // triple-stacking. Order matters: auth first (owns the 401
        // refresh-retry) through api-error last (passes 401 through
        // untouched), then fuse's loading interceptor.
        provideHttpClient(
            withInterceptors([
                ...authInterceptors,
                ...getFuseHttpInterceptors(),
            ])
        ),
        provideRouter(
            appRoutes,
            withInMemoryScrolling({ scrollPositionRestoration: 'enabled' }),
            // kb#2200 (PFL-FE-WEB-G/-2): a tab that survives a deploy keeps
            // requesting lazy chunks by their old content hash; the files are
            // gone, so every lazy navigation fails. One guarded
            // location.assign reload fetches the fresh index.html. Other
            // errors return void here and still reach the Sentry ErrorHandler
            // via the router's normal rethrow path.
            withNavigationErrorHandler((e) =>
                inject(ChunkLoadRecoveryService).handleNavigationError(e)
            )
        ),

        // Transloco Config
        provideTransloco({
            config: {
                // Language-menu labels are autonyms — each language named in
                // itself, whatever the UI language — so a Vietnamese user is
                // never offered the English exonym "Vietnamese" (#3805/#3783).
                availableLangs: [
                    {
                        id: 'en',
                        label: 'English',
                    },
                    {
                        id: 'vn',
                        label: 'Tiếng Việt',
                    },
                ],
                defaultLang: 'vn',
                fallbackLang: 'vn',
                reRenderOnLangChange: true,
                prodMode: !isDevMode(),
            },
            loader: TranslocoHttpLoader,
        }),
        // nera-common's shared notification inbox client is undecorated (its
        // `angular` subpath is built with plain tsc), so the app registers it.
        provideNotificationInbox(),

        provideAppInitializer(() => {
            const translocoService = inject(TranslocoService);
            const paginator = inject(MatPaginatorIntl) as AppPaginatorIntl;
            const datepicker = inject(MatDatepickerIntl) as AppDatepickerIntl;
            const language =
                localStorage.getItem('appLanguage') ||
                translocoService.getDefaultLang();

            translocoService.setActiveLang(language);
            paginator.setLanguage(language);
            datepicker.setLanguage(language);

            return firstValueFrom(translocoService.load(language));
        }),

        // Per-tenant theming (T3.10) — eager instantiation so CSS custom
        // properties / favicon apply as early as possible, not lazily on
        // first injection from some unrelated component.
        provideEnvironmentInitializer(() => inject(TenantThemeService)),

        // The 404/403 "back" button needs the tab's navigation log from the very first
        // navigation (a bad link opened in a new tab), so start recording before it runs.
        provideEnvironmentInitializer(() => inject(SafeBackService)),

        // The HOST's org (branding, org timezone, `TenantContextService.orgId`)
        // used to be resolved as a side effect of `neraTenantInterceptor`'s
        // first `/api/` request. That interceptor is gone — it existed only to
        // inject `X-Organization-Id`/`X-Org-ID`/`X-Org-Slug`, which the backend
        // ignores for an authenticated caller (JWT `org_id` is authoritative,
        // GatewayTenantScopeMiddleware strips client-supplied copies) — so the
        // lookup is kicked off here, explicitly, instead of riding on an
        // interceptor's side effect. Idempotent and sessionStorage-cached, so
        // TenantThemeService's own tenant-host call collapses into this one.
        // Do NOT read the acting org from here: that is
        // `TenantContextService.actingOrgId` (the JWT claim via AuthService).
        provideEnvironmentInitializer(() => {
            void inject(OrgContextService).resolveFromDomain();
        }),

        // PFL-1900: <html lang> must track the active Transloco locale, not
        // stay hardcoded 'en'. Eager instantiation (rather than lazy, e.g.
        // only on first injection from LanguagesComponent) so unauthenticated
        // routes with no header — sign-in included — also get the correct
        // attribute, not just the admin shell.
        provideEnvironmentInitializer(() => inject(DocumentLangService)),

        // Fuse
        provideAuth(),
        provideIcons(),
        provideFuse(fuseProviderConfig),
    ],
};
